import React from "react"
import CabBox from "../CabCardBox"
import { useSelector } from "react-redux"

const fmtDate = (s) => {
  if (!s) return ""
  const d = new Date(s)
  if (isNaN(d.getTime())) return s
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })
}

const fmtTime = (t) => {
  if (!t) return ""
  if (/am|pm/i.test(t)) return t
  const [h, m] = t.split(":").map(Number)
  if (isNaN(h)) return t
  const ampm = h >= 12 ? "PM" : "AM"
  const h12 = h % 12 || 12
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`
}

// Whole-day count of a round trip, inclusive of both the pickup and return day.
// Matches the backend's day floor: ceil(diff in days) + 1 (a 15th->16th trip is
// 2 days). Returns 0 when either date is missing so the chip simply hides it.
const tripDayCount = (start, end) => {
  if (!start || !end) return 0
  const a = new Date(start)
  const b = new Date(end)
  if (isNaN(a.getTime()) || isNaN(b.getTime())) return 0
  a.setHours(0, 0, 0, 0)
  b.setHours(0, 0, 0, 0)
  return Math.max(1, Math.ceil((b - a) / 86400000) + 1)
}

const Chip = ({ path, children }) => (
  <span className="pm-fares__chip">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {path}
    </svg>
    {children}
  </span>
)

const MainCab = ({ tripDetails }) => {
  const { cabs, distance } = useSelector((state) => state.cab)

  // trip_type: 3 = Outstation, 2 = Rental (hourly package), 1 = Local.
  const tripType = Number(tripDetails?.trip_type)
  const isOutstation = tripType === 3
  const isRental = tripType === 2
  const from = tripDetails?.pickup_city
  const to = tripDetails?.drop_city
  const roundTrip = tripDetails?.round_trip === 1 || tripDetails?.round_trip === "1"
  const pkgHours = Number(tripDetails?.package_type_in_hours) || 0
  const cabCount = cabs?.length || 0
  // Round trips are priced per day (300 km/day floor), so the day count is what
  // makes the fare make sense to the rider. One-way and local never show it.
  const tripDays = roundTrip ? tripDayCount(tripDetails?.trip_date, tripDetails?.return_date) : 0

  // Title reads correctly for each product (rental has no drop city; the API
  // returns package hours in the `distance` field, so never show it as km here).
  const title = isRental
    ? <>Choose your <span>{from}</span> rental cab</>
    : isOutstation && to
      ? <>Choose your <span>{from} to {to}</span> cab</>
      : <>Choose your <span>{from}</span> cab</>

  return (
    <section className="pm-fares">
      <div className="pm-fares__head">
        <span className="pm-fares__eyebrow">{roundTrip ? "Fixed base price" : "Fixed price · all inclusive"}</span>
        <h2 className="pm-fares__title">{title}</h2>

        <div className="pm-fares__summary">
          <Chip path={<><path d="M12 21s-7-6.6-7-11a7 7 0 0 1 14 0c0 4.4-7 11-7 11Z" /><circle cx="12" cy="10" r="2.5" /></>}>
            {from}{isOutstation && to ? <> &rarr; {to}</> : null}
          </Chip>
          {tripDetails?.trip_date && (
            <Chip path={<><rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></>}>
              {fmtDate(tripDetails.trip_date)}{tripDetails.trip_time ? `, ${fmtTime(tripDetails.trip_time)}` : ""}
            </Chip>
          )}

          {/* Trip-type specific chip: one-way/round for outstation, package for
              rental, "Local trip" for local. Never one-way for a rental. */}
          {isOutstation && (
            <Chip path={<><path d="M7 4v13M4 14l3 3 3-3M17 20V7M14 10l3-3 3 3" /></>}>
              {roundTrip
                ? `Round trip${tripDays > 0 ? ` · ${tripDays} ${tripDays === 1 ? "day" : "days"}` : ""}`
                : "One way"}
            </Chip>
          )}
          {isRental && pkgHours > 0 && (
            <Chip path={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>}>
              {pkgHours} hrs package
            </Chip>
          )}
          {!isOutstation && !isRental && (
            <Chip path={<><path d="M3 12h18M12 3v18" /></>}>Local trip</Chip>
          )}

          {/* Distance in km is only meaningful for outstation. */}
          {isOutstation && Math.ceil(distance) > 0 && (
            <Chip path={<><path d="M4 21 8 3M20 21 16 3M12 5v3M12 12v3M12 19v1" /></>}>
              {Math.ceil(distance)} km
            </Chip>
          )}
        </div>

        {cabCount > 0 && (
          <p className="pm-fares__direction">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
            Pick a cab below, then tap <strong>Book</strong> to review your trip &amp; confirm.
          </p>
        )}
      </div>

      <div className="pm-fares__grid">
        <CabBox tripDetails={tripDetails} />
      </div>

      {cabCount > 0 && (
        <div className="pm-fares__assure">
          <div className="pm-fares__assure-inner">
            <div className="pm-fares__assure-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3Z" /><path d="m9 12 2 2 4-4" />
              </svg>
              <span><strong>Fixed price</strong>, no surge ever</span>
            </div>
            <div className="pm-fares__assure-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h12v20l-3-2-3 2-3-2-3 2Z" /><path d="M9 8h6M9 12h6" />
              </svg>
              <span>{roundTrip
                ? <><strong>GST included</strong> · toll &amp; driver allowance extra</>
                : <>Tolls, <strong>GST &amp; driver allowance</strong> included</>}</span>
            </div>
            <div className="pm-fares__assure-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M4 13v-1a8 8 0 0 1 16 0v1" /><rect x="2.5" y="13" width="4" height="6" rx="1.5" /><rect x="17.5" y="13" width="4" height="6" rx="1.5" /><path d="M20 19a4 4 0 0 1-4 3h-1.5" />
              </svg>
              <span><strong>24/7 support</strong> · verified drivers</span>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default MainCab
